import { z } from "zod";
import type { UserSummary } from "./types";

/* ───────────── Rail layout: server order and folders ───────────── */

/** Icon keys a folder can use. The web client maps each to an icon; anything else is treated as an emoji. */
export const RAIL_FOLDER_ICONS = [
  "folder",
  "star",
  "heart",
  "gamepad",
  "music",
  "code",
  "book",
  "briefcase",
  "fire",
  "bolt",
  "leaf",
  "moon",
  "sun",
  "globe",
  "chat",
  "palette",
] as const;
export type RailFolderIcon = (typeof RAIL_FOLDER_ICONS)[number];

const railHex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex colour like #4cc38a");

export const railFolderSchema = z.object({
  kind: z.literal("folder"),
  /** Client-generated, stable across edits. */
  id: z.string().min(1).max(40),
  name: z.string().trim().max(32).default(""),
  color: railHex.nullable().default(null),
  /** A RAIL_FOLDER_ICONS key, a single emoji, or null for the default folder look. */
  icon: z.string().trim().max(32).nullable().default(null),
  /** Empty folders are accepted and dropped by normaliseRailLayout. */
  communityIds: z.array(z.string().uuid()).max(200),
});
export const railCommunitySchema = z.object({ kind: z.literal("community"), id: z.string().uuid() });
export const railItemSchema = z.discriminatedUnion("kind", [railCommunitySchema, railFolderSchema]);
export const railLayoutSchema = z.object({ items: z.array(railItemSchema).max(300) });

export type RailFolder = z.infer<typeof railFolderSchema>;
export type RailItem = z.infer<typeof railItemSchema>;

const RAIL_EMOJI_RE = /^(?:\p{Extended_Pictographic}|\p{Regional_Indicator}|\p{Emoji_Modifier}|\u200d|\ufe0f|\u20e3|[#*0-9])+$/u;

/** A folder icon value: a known icon key, a short emoji, or null. Anything else becomes null. */
export function sanitiseRailFolderIcon(icon: string | null | undefined): string | null {
  const v = icon?.trim();
  if (!v) return null;
  if ((RAIL_FOLDER_ICONS as readonly string[]).includes(v)) return v;
  return v.length <= 16 && RAIL_EMOJI_RE.test(v) && /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(v) ? v : null;
}

/**
 * Reconcile a saved rail layout with the communities the user belongs to
 * (`memberIds`, in join order): unknown or left communities are dropped, each
 * community appears once (first occurrence wins), empty folders disappear,
 * folder ids are unique, and communities missing from the layout (new joins)
 * are appended at the end.
 */
export function normaliseRailLayout(items: readonly RailItem[], memberIds: readonly string[]): RailItem[] {
  const valid = new Set(memberIds);
  const seen = new Set<string>();
  const folderIds = new Set<string>();
  const out: RailItem[] = [];
  const take = (id: string) => {
    if (!valid.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  };
  for (const it of items) {
    if (it.kind === "community") {
      if (take(it.id)) out.push({ kind: "community", id: it.id });
      continue;
    }
    const communityIds = it.communityIds.filter(take);
    if (!communityIds.length) continue;
    let id = it.id;
    while (folderIds.has(id)) id = `${it.id.slice(0, 32)}-${folderIds.size}`;
    folderIds.add(id);
    out.push({
      kind: "folder",
      id,
      name: (it.name ?? "").trim().slice(0, 32),
      color: it.color && /^#[0-9a-fA-F]{6}$/.test(it.color) ? it.color.toLowerCase() : null,
      icon: sanitiseRailFolderIcon(it.icon),
      communityIds,
    });
  }
  for (const id of memberIds) if (take(id)) out.push({ kind: "community", id });
  return out;
}

/** Community ids in the order the rail shows them (folders expanded in place). */
export function flattenRailLayout(items: readonly RailItem[]): string[] {
  return items.flatMap((it) => (it.kind === "community" ? [it.id] : it.communityIds));
}

/* ───────────── Polls in chat messages ───────────── */

/** Durations offered in the poll composer, in hours. */
export const MESSAGE_POLL_DURATIONS = [1, 4, 8, 24, 72, 168, 336] as const;

export const createMessagePollSchema = z.object({
  question: z.string().trim().min(1, "Ask a question").max(300),
  options: z
    .array(z.object({ text: z.string().trim().min(1).max(55), emoji: z.string().trim().max(64).optional() }))
    .min(2, "Add at least two answers")
    .max(10),
  multiple: z.boolean().default(false),
  durationHours: z.number().int().min(1).max(24 * 14).default(24),
});
export type CreateMessagePoll = z.infer<typeof createMessagePollSchema>;

export const messagePollVoteSchema = z.object({ optionIds: z.array(z.string().min(1).max(40)).max(10) });

export interface MessagePoll {
  question: string;
  options: { id: string; text: string; emoji: string | null; votes: number }[];
  multiple: boolean;
  endsAt: string;
  closed: boolean;
  totalVoters: number;
  /** Option ids the viewer voted for. */
  myVotes: string[];
}

/** One page of the people who picked a poll answer (GET /api/messages/:id/poll/voters). */
export interface MessagePollVoters {
  optionId: string;
  items: UserSummary[];
  /** Pass as `after` for the next page; null on the last one. */
  nextCursor: string | null;
}

/* ───────────── GIFs ───────────── */

export interface GifResult {
  id: string;
  title: string;
  /** Full-size animation to send. */
  url: string;
  /** Small looping preview for the picker. */
  previewUrl: string;
  /** First frame, for when animation is off. */
  stillUrl?: string;
  width: number;
  height: number;
}

export interface GifPage {
  items: GifResult[];
  /** Opaque cursor for the next page, or null at the end. */
  next: string | null;
}

/** GIF providers the server can proxy. Klipy is a stub until its adapter lands. */
export const GIF_PROVIDERS = ["giphy", "tenor", "klipy"] as const;
export type GifProviderName = (typeof GIF_PROVIDERS)[number];

/**
 * Hosts GIF media may be served from. One list for server-side validation of
 * sent GIFs, client-side rendering and the production CSP.
 * TODO(klipy): add Klipy's CDN host(s) once the adapter is implemented.
 */
export const GIF_MEDIA_HOSTS = [
  "media.giphy.com",
  "media0.giphy.com",
  "media1.giphy.com",
  "media2.giphy.com",
  "media3.giphy.com",
  "media4.giphy.com",
  "i.giphy.com",
  "media.tenor.com",
  "media1.tenor.com",
  "c.tenor.com",
] as const;

/** An https URL on an allow-listed GIF host. */
export function isGifMediaUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === "https:" && !u.username && !u.password && !u.port && (GIF_MEDIA_HOSTS as readonly string[]).includes(u.hostname);
  } catch {
    return false;
  }
}
